import {
  decodeCellValue,
  encodeCellValue,
  formatCellDisplay,
  isEditableColumnType,
  isPhase1ColumnType,
  isRichColumnType,
  type DateFormat,
  type PropertyValuePayload,
  type SelectOption,
} from '@/lib/databases/values';
import { createElement, useEffect, useRef, useState } from 'react';
import { Pressable, Text, View } from 'react-native';

import type { BlockPropertyRecord } from '@/lib/databases/types';
import type { DatabaseColumnDefinition } from '@/lib/databases/types';

import { DateCellEditor } from './DateCellEditor.web';
import { RelationCellEditor } from './RelationCellEditor.web';
import { SelectCellEditor } from './SelectCellEditor.web';

type DatabaseCellProps = {
  column: DatabaseColumnDefinition;
  property: BlockPropertyRecord | undefined;
  disabled?: boolean;
  isEditing: boolean;
  onStartEdit: () => void;
  onEndEdit: () => void;
  onSave: (value: Record<string, unknown> | null) => Promise<void>;
  onEnsureSelectOption?: (columnId: string, name: string) => Promise<SelectOption>;
  onDateFormatChange?: (columnId: string, format: DateFormat) => void;
  variant?: 'table' | 'panel';
};

function isCenterAlignedColumnType(type: string): boolean {
  return type === 'checkbox' || type === 'date';
}

function cellRowClassName(type: string): string {
  const base = 'flex min-w-0 max-w-full flex-row h-full min-h-[32px] w-full overflow-hidden px-2 py-1';
  if (isCenterAlignedColumnType(type)) {
    return `${base} items-center justify-center`;
  }
  return `${base} items-center justify-start`;
}

function cellInputClassName(type: string): string {
  return isCenterAlignedColumnType(type)
    ? 'cs-db-cell-input cs-db-cell-input-center'
    : 'cs-db-cell-input';
}

function payloadFromProperty(
  property: BlockPropertyRecord | undefined,
): PropertyValuePayload | null {
  if (!property?.value || typeof property.value !== 'object' || !('t' in property.value)) {
    return null;
  }
  return property.value as PropertyValuePayload;
}

export function DatabaseCell({
  column,
  property,
  disabled,
  isEditing,
  onStartEdit,
  onEndEdit,
  onSave,
  onEnsureSelectOption,
  onDateFormatChange,
  variant = 'table',
}: DatabaseCellProps) {
  const payload = payloadFromProperty(property);
  const decoded = decodeCellValue(column.type, payload, { columnConfig: column.config });
  const [draft, setDraft] = useState(decoded.text);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const panelMode = variant === 'panel';

  useEffect(() => {
    setDraft(decoded.text);
  }, [decoded.text, property?.syncVersion]);

  useEffect(() => {
    if (isEditing && !isRichColumnType(column.type)) {
      setDraft(decoded.text);
      inputRef.current?.focus();
    }
  }, [isEditing, decoded.text, column.type]);

  async function commitText(options?: { blur?: boolean }) {
    if (saving || disabled) {
      return;
    }
    const encoded = encodeCellValue(column.type, draft);
    if (encoded === null && column.type === 'number') {
      if (!panelMode) {
        onEndEdit();
      }
      if (options?.blur) {
        inputRef.current?.blur();
      }
      return;
    }
    setSaving(true);
    try {
      await onSave(encoded ? (encoded as Record<string, unknown>) : null);
      if (!panelMode) {
        onEndEdit();
      }
    } finally {
      setSaving(false);
      if (options?.blur) {
        inputRef.current?.blur();
      }
    }
  }

  function handleInputKeyDown(
    e: { key: string; preventDefault: () => void; stopPropagation: () => void },
    inputEl: HTMLInputElement | null,
  ) {
    if (e.key === 'Enter') {
      e.preventDefault();
      void commitText({ blur: true }).then(() => {
        inputEl?.blur();
      });
      return;
    }
    if (e.key === 'Escape') {
      setDraft(decoded.text);
      if (!panelMode) {
        onEndEdit();
      }
      inputEl?.blur();
    }
    e.stopPropagation();
  }

  async function toggleCheckbox() {
    if (saving || disabled || !isPhase1ColumnType(column.type) || column.type !== 'checkbox') {
      return;
    }
    setSaving(true);
    try {
      const next = encodeCellValue('checkbox', !decoded.bool);
      if (next) {
        await onSave(next as Record<string, unknown>);
      }
    } finally {
      setSaving(false);
    }
  }

  if (column.type === 'rollup') {
    const display = formatCellDisplay(column.type, payload ?? undefined, {
      columnConfig: column.config,
    });
    return createElement(
      'div',
      {
        className:
          'flex min-w-0 max-w-full flex-row h-full min-h-[32px] w-full overflow-hidden items-center justify-start px-2 py-1 text-sm text-fg-muted',
      },
      display || '\u00a0',
    );
  }

  if (column.type === 'select' || column.type === 'multi_select') {
    return createElement(SelectCellEditor, {
      column,
      property,
      disabled,
      saving,
      isOpen: isEditing,
      onOpen: onStartEdit,
      onClose: onEndEdit,
      onSave: async (value) => {
        setSaving(true);
        try {
          await onSave(value);
        } finally {
          setSaving(false);
        }
      },
      onEnsureOption: onEnsureSelectOption
        ? (name) => onEnsureSelectOption(column.id, name)
        : undefined,
      variant,
    });
  }

  if (column.type === 'relation') {
    return createElement(RelationCellEditor, {
      column,
      property,
      disabled,
      saving,
      isOpen: isEditing,
      onOpen: onStartEdit,
      onClose: onEndEdit,
      onSave: async (value) => {
        setSaving(true);
        try {
          await onSave(value);
        } finally {
          setSaving(false);
        }
      },
      variant,
    });
  }

  if (column.type === 'date') {
    return createElement(DateCellEditor, {
      column,
      property,
      disabled,
      saving,
      isOpen: isEditing,
      onOpen: onStartEdit,
      onClose: onEndEdit,
      onSave: async (value) => {
        setSaving(true);
        try {
          await onSave(value);
        } finally {
          setSaving(false);
        }
      },
      onDateFormatChange: onDateFormatChange
        ? (format) => onDateFormatChange(column.id, format)
        : undefined,
      variant,
    });
  }

  if (column.type === 'checkbox' && isPhase1ColumnType(column.type)) {
    if (panelMode) {
      return (
        <label className="cs-db-row-panel-checkbox">
          <input
            type="checkbox"
            checked={decoded.bool}
            disabled={disabled || saving}
            onChange={() => void toggleCheckbox()}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          />
        </label>
      );
    }
    return (
      <Pressable
        onPress={() => void toggleCheckbox()}
        disabled={disabled || saving}
        className={cellRowClassName(column.type)}
      >
        <View
          className={`h-4 w-4 rounded border ${
            decoded.bool ? 'border-fg bg-fg' : 'border-border-strong bg-surface'
          }`}
        />
      </Pressable>
    );
  }

  if (panelMode && isEditableColumnType(column.type)) {
    const inputType = column.type === 'number' ? 'number' : 'text';
    return (
      <input
        ref={inputRef}
        type={inputType}
        value={draft}
        disabled={disabled || saving}
        className="cs-db-row-panel-input"
        placeholder={column.type === 'number' ? '0' : ''}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => void commitText()}
        onKeyDown={(e) =>
          handleInputKeyDown(e, e.currentTarget as HTMLInputElement)
        }
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      />
    );
  }

  if (panelMode) {
    const display = formatCellDisplay(column.type, payload ?? undefined, {
      columnConfig: column.config,
    });
    return createElement(
      'div',
      { className: 'cs-db-row-panel-input cs-db-row-panel-readonly' },
      display || '\u00a0',
    );
  }

  if (isEditing && isEditableColumnType(column.type)) {
    const inputType = column.type === 'number' ? 'number' : 'text';
    return (
      <input
        ref={inputRef}
        type={inputType}
        value={draft}
        disabled={disabled || saving}
        className={cellInputClassName(column.type)}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => void commitText()}
        onKeyDown={(e) =>
          handleInputKeyDown(e, e.currentTarget as HTMLInputElement)
        }
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      />
    );
  }

  const display = formatCellDisplay(column.type, payload ?? undefined, {
    columnConfig: column.config,
  });

  return (
    <Pressable
      onPress={() => {
        if (!disabled && isEditableColumnType(column.type)) {
          onStartEdit();
        }
      }}
      className={cellRowClassName(column.type)}
    >
      {display ? (
        <Text className="min-w-0 w-full truncate text-left text-sm text-fg-muted">{display}</Text>
      ) : null}
    </Pressable>
  );
}
